import { DatePipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, Input, inject } from "@angular/core";
import { ButtonComponent } from "@components/button/button.component";
import { HeaderComponent } from "@components/header/header.component";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { INvbUtskriftItem, INvbVariantConfig } from "../../../../models/naturvardesbedomning-models";

export type NvbUtskriftVariant = "norra" | "mellersta" | "sodra" | "riskbedomning";

const NVB_VARIANT_CONFIG: Record<Exclude<NvbUtskriftVariant, "riskbedomning">, INvbVariantConfig> = {
    norra: {
        label: "Norra Sverige",
        aldersgrans: 200,
        visaDodVedKvalitetTrsl: true,
        visaDodVedTrsl: false,
        visaNaturvardestradTrsl: false
    },
    mellersta: {
        label: "Mellersta Sverige",
        aldersgrans: 180,
        visaDodVedKvalitetTrsl: true,
        visaDodVedTrsl: false,
        visaNaturvardestradTrsl: false
    },
    sodra: {
        label: "Södra Sverige",
        aldersgrans: 160,
        visaDodVedKvalitetTrsl: false,
        visaDodVedTrsl: true,
        visaNaturvardestradTrsl: true
    }
};

@Component({
    selector: "rask-nvb-utskrift-dialog",
    templateUrl: "./nvb-utskrift-dialog.component.html",
    styleUrls: ["./nvb-utskrift-dialog.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        HeaderComponent,
        ButtonComponent,
        DatePipe
    ]
})
export class NvbUtskriftDialogComponent {
    @Input() items: INvbUtskriftItem[] = [];

    private readonly modal = inject(NgbActiveModal);

    protected bedomningPa(item: INvbUtskriftItem): string {
        const value = item.naturvardesbedomning?.bedomningSno;
        if (value === null || value === undefined) {
            return "-";
        }
        return value ? "Barmark" : "Snö";
    }

    protected close(): void {
        this.modal.dismiss();
    }

    protected getNaturvardeSummering(item: INvbUtskriftItem): string {
        if (!item.naturvardesbedomning || this.isRiskbedomning(item)) {
            return "";
        }

        if (item.naturvardesbedomning.hansynskravandeBiotop === true) {
            return "Alltid skyddad enligt lag, ska avsättas som naturvård";
        }

        const values = [
            item.naturvardesbedomning.naturvardestrad,
            item.naturvardesbedomning.naturvardestradKvalitet,
            item.naturvardesbedomning.dodVed,
            item.naturvardesbedomning.dodVedKvalitet,
            item.naturvardesbedomning.alder,
            item.naturvardesbedomning.kontinuitet,
            item.naturvardesbedomning.tradslag
        ];

        const antal = values.filter(Boolean).length;
        switch (antal) {
            case 0:
                return "Naturvärden kan sannolikt tas om hand genom generell eller förstärkt miljöhänsyn";
            case 1:
                return "Vissa naturvärden finns. Naturvärden kan troligen tas om hand genom generell eller förstärkt miljöhänsyn";
            case 2:
                return "Vissa naturvärden finns. Möjligen höga naturvärden, använd second opinion vid behov";
            default:
                return "Det finns höga naturvärden, sannolikt nyckelbiotop";
        }
    }

    protected getVariantConfig(item: INvbUtskriftItem): INvbVariantConfig | null {
        return this.isRiskbedomning(item) ? null : NVB_VARIANT_CONFIG[item.variant as Exclude<NvbUtskriftVariant, "riskbedomning">];
    }

    protected isRiskbedomning(item: INvbUtskriftItem): boolean {
        return item.variant === "riskbedomning";
    }

    protected jaNej(value: boolean | null | undefined): string {
        return value ? "Ja" : "Nej";
    }

    protected print(): void {
        window.print();
    }
}
